// Firefox and Zen use Gecko's desktop capture implementation. Keep the existing
// recording path for Chromium, Safari, and mobile Firefox (which uses WebKit on iOS).
export function usesFirefoxRecordingCompatibility(userAgent = navigator.userAgent) {
  return /(?:Firefox|Zen)\/\d/i.test(userAgent) && !/Android|Mobile|iPhone|iPad|FxiOS|Chrome|Chromium|Edg\/|OPR\//i.test(userAgent);
}

export function createFirefoxRecordingCapture(mediaDevices, createAudioContext) {
  let stream = null, context = null, cancelled = false;
  const stop = () => {
    cancelled = true;
    stream?.getTracks().forEach(track => track.stop());
    stream = null;
    if (context && context.state !== 'closed') {
      try {Promise.resolve(context.close()).catch(() => {});} catch {}
    }
  };
  const assertActive = () => {
    if (cancelled || !stream?.getVideoTracks().some(track => track.readyState !== 'ended')) {
      throw new Error('Screen sharing ended before recording started. Select your screen again.');
    }
  };
  return {
    stop, assertActive,
    get stream() {return stream;},
    get audioContext() {return context;},
    async start() {
      try {
        // Both calls happen directly during the user's click, before any network work.
        context = createAudioContext();
        const audioReady = context.state === 'suspended' ? context.resume() : Promise.resolve();
        // Attach a handler immediately while the capture permission picker is open.
        const resumed = Promise.resolve(audioReady).then(() => null, error => error);
        const selected = await mediaDevices.getDisplayMedia({
          video: {width: {ideal: 1920, max: 1920}, height: {ideal: 1080, max: 1080}, frameRate: {ideal: 60, max: 60}},
          // Shared audio is unavailable in Firefox/Zen. Microphone and optional
          // loopback input are mixed separately through getUserMedia.
          audio: false,
        });
        if (cancelled) {selected.getTracks().forEach(track => track.stop()); throw new Error('Recording setup was closed.');}
        stream = selected;
        const audioError = await resumed;
        if (audioError) throw audioError;
        assertActive();
        return stream;
      } catch (error) {stop(); throw error;}
    },
  };
}
